import { fetchBestPriceForOffer } from 'actions/OfferActions'
import config from 'constants/config'
import GeoContext from 'contexts/geoContext'
import useOffer from 'hooks/Offers/useOffer'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useBedbankRates from 'hooks/useBedbankRates'
import { isFlightForceBundled } from 'lib/isFlightForceBundled'
import offerPageURL from 'lib/offerPageURL'
import { isBedbank, isHotel, isLEHotel } from 'lib/offerTypes'
import { buildSearchParamsFromFilters } from 'lib/searchUtils'
import { useEffect, useMemo, useContext } from 'react'
import { shallowEqual } from 'react-redux'
import { HotLeadPriceData, getBedbankPriceData, getLeHotelPriceData } from 'selectors/recomendations/hotLeadSelectors'

type HotLeadSupportedType = App.HotelOffer | App.HotelOfferSummary | App.BedbankOffer | App.BedbankOfferSummary

function isCountryEnabled(currentCountry: string) {
  const supportedCountries = new Set(config.LERE_STICKY_HOT_LEAD_ENABLED_COUNTRIES?.split(',') || ['GB', 'US'])
  return supportedCountries.has(currentCountry)
}

export interface HotLeadDataView {
  offer: HotLeadSupportedType,
  checkIn?: string,
  checkOut?: string,
  duration: number,
  occupancy: App.Occupants[],
  price: number,
  discountPercent: number,
  offerUrl: string,
}

// currently we support hotel only
export function isHotLeadSupported(offer: App.AnyOffer): offer is HotLeadSupportedType {
  return isHotel(offer) && !isFlightForceBundled(offer)
}

interface HotLeadData {
  hotLeadData?: HotLeadDataView;
  hotLeadDataReady: boolean;
}

export function useHotLeadData(hotLead:App.HotLeadOffer, isHotLeadEnabled: boolean = true): HotLeadData {
  const dispatch = useAppDispatch()
  const {
    offerId,
    checkIn,
    checkOut,
    occupancy,
  } = hotLead

  const { currentRegionCode } = useContext(GeoContext)
  const canUseHotLeadData = (
    isHotLeadEnabled &&
    isCountryEnabled(currentRegionCode) &&
    config.LERE_STICKY_HOT_LEAD_ENABLED
  )

  const [offer, fetching] = useOffer(offerId)
  const validOffer = !!offer && isHotLeadSupported(offer)
  const isLeHotel = isLEHotel(offer)
  const isBedbankHotel = isBedbank(offer)
  const hasDates = !!checkIn && !!checkOut
  const validHotLead = (
    canUseHotLeadData &&
    (fetching || validOffer) &&
    !!occupancy && // all HL must have occupancy
    occupancy.length > 0 &&
    (!isBedbankHotel || hasDates) // bedbank HL must also have dates
  )

  // calculate the best price for LE offers (not bedbank)
  useEffect(() => {
    if (isLeHotel && validHotLead && !!offer && hasDates) {
      dispatch(fetchBestPriceForOffer(offer, {
        checkIn,
        checkOut,
        occupants: occupancy,
      }))
    }
  }, [dispatch, checkIn, checkOut, occupancy, hasDates, offer, isLeHotel, validHotLead])

  const leHotelArgs = useMemo(() => ({ offer, validHotLead, hotLead }), [offer, validHotLead, hotLead])
  const leHotelPriceData = useAppSelector<HotLeadPriceData>(
    (state) => getLeHotelPriceData(state, leHotelArgs),
    shallowEqual,
  )

  // calculate bedbank price
  const [bedbankRates, fetchingBedbankRates] = useBedbankRates(offerId, occupancy, checkIn, checkOut, isBedbankHotel)
  const bedbankArgs = useMemo(() => (
    { offer, validHotLead, bedbankRates, fetchingBedbankRates }
  ), [offer, validHotLead, bedbankRates, fetchingBedbankRates])
  const bedbankPriceData = useAppSelector<HotLeadPriceData>(
    (state) => getBedbankPriceData(state, bedbankArgs),
    shallowEqual,
  )

  const offerUrl = useMemo(() => {
    if (validHotLead && !!offer) {
      const filters = {
        checkIn,
        checkOut,
        rooms: occupancy,
      }
      const queryParams = buildSearchParamsFromFilters(filters)

      return offerPageURL(offer, queryParams)
    }
  }, [checkIn, checkOut, occupancy, offer, validHotLead])

  const {
    totalPrice,
    discountPercent,
    duration,
    isSoldOut,
  } = isLeHotel ? leHotelPriceData : bedbankPriceData

  const validData = (
    !!offer &&
    !!duration &&
    !!totalPrice &&
    !!offerUrl
  )

  const hotLeadDataReady = !validHotLead || !validOffer || isSoldOut || validData

  const hotLeadData: HotLeadDataView | undefined = (validHotLead && validOffer && validData) ? {
    offer,
    checkIn,
    checkOut,
    duration,
    occupancy,
    price: totalPrice,
    discountPercent,
    offerUrl,
  } : undefined

  return {
    hotLeadData,
    hotLeadDataReady,
  }
}
